import { Injectable } from '@angular/core'
import { environment } from 'src/environments/environment'
@Injectable({
  providedIn: 'root'
})
export class HelpersService {
  builderURL = environment.builderURL
  stacksURL = environment.stacksURL

  constructor() {}

  /**
   * Clones the object to assure that we didn't manipulate the original object
   * @param object the object that needs to be cloned
   * @returns the cloned object
   */
  cloneObject = (object: any) => {
    return JSON.parse(JSON.stringify(object))
  }

  getBuilderUrl = () => {
    return this.builderURL
  }

  toObject = (arr: any) => {
    var rv: any = {}
    for (var i = 0; i < arr.length; ++i) if (arr[i] !== undefined) rv[i] = arr[i]
    return rv
  }

  isZohoImage(url: string): boolean {
    return url.includes('inventory.zoho.com/DocTemplates_ItemImage_')
  }

  // Generate a random string of a specified length
  generateRandomString = (length: number) => {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    let result = ''
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length)
      result += charset.charAt(randomIndex)
    }
    return result
  }

  getLastItem(items: any[]): any {
    return items?.[items.length - 1] || null
  }

  public parseAccess(jsonString: string): any {
    if(!jsonString) {
      return null
    }
    try {
      const pseudoJson = jsonString.trim()
      const validJson = pseudoJson.replace(/(\w+)(?=\s*:)/g, '"$1"').replace(/'/g, '"')
      return JSON.parse(validJson)
    } catch (error) {
      console.error('Error parsing access JSON:', error)
      return null
    }
  }

  public isArrayOfStrings(arr: any) {
    return Array.isArray(arr) && arr.length > 0 && arr.every(item => typeof item === 'string');
  }

  public isArrayOfIntegers(arr: any) {
    return Array.isArray(arr) && arr.length > 0 && arr.every(item => Number.isInteger(item));
  }


  getArrayValue(value: any): any {
    if (Array.isArray(value)) {
      return value[0];
    }
    return value;
  }

  public extractProjectIdFromUrl = (url: string): string | null => {
    try {
      const decodedUrl = decodeURIComponent(url)
      // project_assets/ = legacy Firebase URLs, project-assets/ = Supabase bucket URLs
      return decodedUrl.match(/project[_-]assets\/(\d+)\//)?.[1] ?? null
    } catch (e) {
      return null
    }
  }
  // Helper function to sanitize text so it can be used as a Firebase key
  public sanitizeKey(key: string): string {
    // Replace invalid Firebase path characters with underscores (or any valid character)
    return key.replace(/[.#$[\]]/g, '')
  }
  /*
   * generateValidKey: Generates a valid key from the input string by replacing
   * specific characters (such as #, $, /, [, ], and spaces) with spaces.
   * - Returns the sanitized string, which can be used as a valid key in translations.
   */
  public generateValidKey(input: string): string {
    // Replace control characters (newline, tab, etc.) with space so they don't get rejected by canBeSavedToFirebase
    // Replace periods (.) with "_dot_", and other invalid characters with a space
    return input.replace(/[\x00-\x1F\x7F]+/g, ' ').replace(/[.]|[# $\/\[\] ]/g, (match) => (match === '.' ? '_dot_' : ' '))
  }

  public canBeSavedToFirebase(key: string): boolean {
    if (!key) return false

    // Firebase forbidden characters
    if (/[.#$\[\]\/]/.test(key)) return false

    // ❗ Reject control characters (newline, tab, etc.)
    if (/[\x00-\x1F\x7F]/.test(key)) return false

    return true
  }
  /** Convert any mix of strings/arrays into a flat array of trimmed strings. */
  public flattenAndTrimToStrings(input: any[]): string[] {
    const results: string[] = []
    this.recursivelyFlatten(input, results)
    return results
  }

  /** Recursively flatten nested arrays and collect valid trimmed strings. */
  public recursivelyFlatten(input: any[], out: string[]): void {
    for (const item of input) {
      if (Array.isArray(item)) {
        this.recursivelyFlatten(item, out)
      } else {
        const s = this.toTrimmedString(item)
        if (s) out.push(s)
      }
    }
  }

  /** Utility: safely convert any value to a trimmed string, or null if invalid */
  public toTrimmedString(value: any): string | null {
    if (typeof value === 'string') {
      const trimmed = value.trim()
      return trimmed ? trimmed : null
    }
    if (value != null) {
      const fallback = String(value).trim()
      return fallback ? fallback : null
    }
    return null
  }

  /**
   * Normalize category / subcategory ids to an array of atomic strings.
   * Handles strings, arrays, mixed values, and comma-separated inputs.
   */
  public normalizeIds(input: any): string[] {
    if (!input) return []

    // Case 1: already an array
    if (Array.isArray(input)) {
      return input
        .flatMap((item) => (typeof item === 'string' ? item.split(',') : [item]))
        .map((v) => String(v).trim())
        .filter((v) => v.length > 0)
    }

    // Case 2: string (CSV or single value)
    if (typeof input === 'string') {
      return input
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v.length > 0)
    }

    // Case 3: fallback (number, etc.)
    return [String(input).trim()].filter(Boolean)
  }

  /**
   * Get current date as DD-MM-YYYY HH:mm:ss string
   */
  public getCurrentDateString(): string {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  }

  /**
   * ── Product-card helpers ───────────────────────────────────────────────
   * Every surface that renders a builder product grid asks the same three
   * questions about a product. They used to be inline boolean chains repeated
   * a dozen times per template — `product?.productStock !== '' &&
   * product?.productStock !== '0' && ...` — which is exactly how they drifted
   * apart between the builder, the website and the native app.
   */
  public hasVariations(product: any): boolean {
    return Array.isArray(product?.productVariations) && product.productVariations.length > 1
  }

  /**
   * Stock arrives as `''`, `'0'`, `0` or a `stock_status` flag depending on
   * which importer wrote the product, so all four mean the same thing. A missing
   * `productStock` (null/undefined) is not one of them: it means stock isn't
   * tracked, and the app cart adds such a product as unlimited (`-1`).
   */
  public isOutOfStock(product: any): boolean {
    if (product?.stock_status === 'outofstock') {
      return true
    }
    const stock = product?.productStock
    if (stock === null || stock === undefined) {
      return false
    }
    return stock === '' || Number(stock) === 0
  }

  /**
   * Absolute money saved on this product, or null when there is no genuine
   * discount. Prices arrive as strings on some surfaces, hence the coercion.
   */
  public getSavings(product: any): number | null {
    const regular = Number(product?.regularPrice)
    const sale = Number(product?.salePrice)
    if (!isFinite(regular) || !isFinite(sale) || sale <= 0 || regular <= 0 || sale >= regular) {
      return null
    }
    return Math.round((regular - sale) * 100) / 100
  }

  /**
   * Savings for the Clean Style card only, so the template needs a single call
   * instead of an `&&` chain whose `as` binding is easy to read wrong.
   */
  public cleanSavings(style: any, product: any): number | null {
    if (style?.products_style !== 'cleanStyle') {
      return null
    }
    return this.getSavings(product)
  }

  /**
   * Flips a card into its short-lived "added" confirmation state. The builder
   * preview has no real cart, so there this is the whole interaction; on the
   * storefronts the flag is set once the add actually succeeds.
   */
  public flashAdded(product: any, ms = 1500): void {
    if (!product) {
      return
    }
    product.justAdded = true
    setTimeout(() => {
      product.justAdded = false
    }, ms)
  }
}
